import {inject, Injectable} from '@angular/core';
import {createDirectus, readItems, rest} from '@directus/sdk';
import {firstValueFrom, forkJoin, from, Observable, of, throwError} from 'rxjs';
import Keycloak from 'keycloak-js';
import {HeaderMenu} from './models/header-menu';
import {FooterLink} from './models/footer-link';
import {Translation} from './models/translation';
import {Language} from './models/language';
import {TextTemplate} from './models/text-template';
import {DIRECTUS_URL} from './directus-url.token';
import {LanguageCode} from './models/language-code';
import {CountryCode} from './models/country-code';
import {
  HrCatalogReferenceData,
  flattenProjectStepResponse,
  mapHrToolFilters,
  mapHrToolMiniResponse,
  mapHrToolResponse,
} from './directus.helper';
import {Module, ModuleFunction} from './models/module-function';
import {HrTool, HrToolFilters, HrToolServiceModel} from './models/hr-tool';
import {BASE_PATH, HrCatalogControllerService} from '@frontend/shared-api';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {
  FlattenedProjectStep,
  ProjectChapter,
  ProjectStep,
  ProjectStepDescription,
} from './models/project-step';
import {ToastService} from '../toast';
import {DirectusSelect, DirectusSelectValue} from './models/directus-select.model';
import {DesignCriteria} from './models/design-criteria';
import {EvaluationCriteria} from './models/evaluation-criteria';
import {Tooltip} from './models/tooltip';
import {ProjectTimeFrameDirectus} from './models/project-time-frame-directus';
import {HostingLocation} from './models/hosting-location';
import {SubcriteriaAttributeDirectus} from './models/evaluation-sub-criteria-attribute';
import {AttributeFulfillmentTypeDirectus} from './models/attribute-fulfillment-type';
import {EvaluationSubCriteriaDirectus} from './models/evaluation-sub-criteria-directus';
import {HrToolMini} from './models/hr-tool-mini';
import {NonHrSystems} from './models/non-hr-systems';
import {PaymentPlan} from './models/payment-plan';
import {
  DirectusDropdown,
  DirectusTranslatedItem,
  DirectusTranslatedItemGroup,
} from './models/directus-dropdown';
import {ProviderPaymentPlan} from './models/provider-payment-plan';
import {DemonstrationAgenda} from './models/demonstration-agenda';
import {CurrencyCode} from './models/currency-code';
import {ImplementationActivity} from './models/ImplementationActivity';
import {ArchitecturalPrincipleDirectus} from './models/architectural-principle';
import {InvitationUploadDocument} from './models/invitation-upload-document';
import {RfxDetailReason, RfxMainReason, RfxStage, RfxTextConnector} from './models/rfx-reasons';
import {UseCasePersona} from './models/use-case-persona';

// Directus API request parameter types
interface DirectusRequestParams {
  limit?: number;
  fields?: (string | Record<string, unknown>)[];
  filter?: Record<string, unknown>;
  sort?: string[];
  [key: string]: unknown;
}

// Type-safe collection names (subset of commonly used ones)
type DirectusCollection =
  | 'languages'
  | 'language_code'
  | 'country_code'
  | 'hr_tool'
  | 'hr_tool_service_model'
  | 'project_steps'
  | 'project_step_description'
  | 'text_template'
  | 'dropdown'
  | 'subcriteria'
  | 'testTable'
  | 'hr_tool_hr_tool_image'
  | 'hr_tool_image'
  | 'hosting_location'
  | 'translations'
  | 'headerMenu'
  | 'footerLinks'
  | 'module_function'
  | 'module'
  | 'media'
  | 'design_criteria'
  | 'criteria'
  | 'subcriteria_attribute'
  | 'tooltip'
  | 'project_time_frame'
  | 'attribute_fulfillment_type'
  | 'non_hr_tools'
  | 'customer_payment_plan'
  | 'demonstration_agenda'
  | 'use_case_persona'
  | 'demonstration_evaluation_scenario'
  | 'provider_payment_plan'
  | 'hr_tool_certification'
  | 'hr_tool_certification_translations'
  | 'hr_tool_hr_tool_certification'
  | 'hr_tool_provider'
  | 'currencies'
  | 'implementation_activity'
  | 'project_decision_reason'
  | 'architectural_principle'
  | 'invitation_upload_documents'
  | 'rfx_main_reasons'
  | 'rfx_detail_reasons'
  | 'rfx_text_connectors'
  | 'rfx_main_reasons_translations_1'
  | 'rfx_detail_reasons_translations'
  | 'rfx_text_connectors_translations';

// caution: when defining fields, the order is important, has to be same order as in response
// e.g.  fields: ['*', {is_this_it: ['*']}, {language_code_translations: ['*']}]
@Injectable({providedIn: 'root'})
export class DirectusService {
  private readonly directusUrl = inject(DIRECTUS_URL);
  private readonly toast = inject(ToastService);
  private readonly keycloak = inject(Keycloak);
  private readonly hrCatalog = inject(HrCatalogControllerService);
  private readonly basePath = inject(BASE_PATH);

  private readonly directus = createDirectus(this.directusUrl).with(
    rest({
      onResponse: (data) => {
        return data;
      },
      onRequest: (options) => {
        if (this.keycloak?.authenticated) {
          options.headers = {...options.headers, 'Authorization': 'Bearer ' + this.keycloak.token};
        }
        return options;
      },
    }),
  );

  // directus default limit = 100; limit=-1 means "no limit"
  private readonly noLimit = -1;

  // Cache for frequently accessed data to prevent duplicate calls
  private evaluationCriteriaCache$?: Observable<EvaluationCriteria[]>;
  private subCriteriaCache$?: Observable<EvaluationSubCriteriaDirectus[]>;
  private subCriteriaAttributesCache$?: Observable<SubcriteriaAttributeDirectus[]>;
  private attributeFulfillmentTypesCache$?: Observable<AttributeFulfillmentTypeDirectus[]>;
  private hrCatalogReferenceCache$?: Observable<HrCatalogReferenceData>;

  private get defaultParams() {
    return {
      limit: this.noLimit,
    };
  }

  getImageUrl(id: string) {
    return this.directusUrl + '/assets/' + id;
  }

  getHrCatalogImageUrl(fileId: string) {
    return `${this.basePath}/hr-catalog/images/${fileId}`;
  }

  loadLanguages(): Observable<Language[]> {
    return this.requestWithErrorHandling<Language[]>('languages', this.defaultParams);
  }

  loadLanguageCodes(): Observable<LanguageCode[]> {
    const params = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
    };
    return this.requestWithErrorHandling<LanguageCode[]>('language_code', params);
  }

  loadLanguageOptions(): Observable<DirectusTranslatedItem[]> {
    const params = {
      ...this.defaultParams,
      fields: ['isoCode', 'translations.language', 'translations.name'],
      sort: ['isoCode'],
    };

    return this.requestWithErrorHandling<LanguageCode[]>('language_code', params).pipe(
      map((languageCodes) =>
        languageCodes.map((lc) => this.toDirectusTranslatedItem(lc, (x) => x.isoCode)),
      ),
    );
  }

  loadCountryCodes(): Observable<CountryCode[]> {
    const params = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
    };
    return this.requestWithErrorHandling<CountryCode[]>('country_code', params);
  }

  loadCountryOptions(): Observable<DirectusTranslatedItem[]> {
    const params = {
      ...this.defaultParams,
      fields: ['isoCode', 'translations.language', 'translations.name'],
      sort: ['isoCode'],
    };

    return this.requestWithErrorHandling<CountryCode[]>('country_code', params).pipe(
      map((countryCodes) =>
        countryCodes.map((cc) => this.toDirectusTranslatedItem(cc, (x) => x.isoCode)),
      ),
    );
  }

  loadCurrencies(): Observable<CurrencyCode[]> {
    const params = {
      ...this.defaultParams,
      fields: ['isoCode', 'translations.language', 'translations.name'],
      sort: ['isoCode'],
    };

    return this.requestWithErrorHandling<CurrencyCode[]>('currencies', params);
  }

  loadModuleFunctionBy(ids: number[]): Observable<ModuleFunction[]> {
    return this.loadModuleFunctions().pipe(
      map((functions: ModuleFunction[]) => functions.filter((func) => ids.includes(func.id))),
    );
  }

  loadImplementationActivities(): Observable<ImplementationActivity[]> {
    const params = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
      sort: ['sort'],
    };

    return this.requestWithErrorHandling<ImplementationActivity[]>(
      'implementation_activity',
      params,
    ).pipe(map((res) => res));
  }

  private toDirectusTranslatedItem<T>(item: T, getId: (item: T) => string) {
    const translations = (item as any).translations; // never undefined or null

    return {
      id: getId(item),
      translations: translations,
      filterText: translations.map((x: any) => x.name).join(' -'),
    } as DirectusTranslatedItem;
  }

  loadFunctionOptions(): Observable<DirectusTranslatedItem[]> {
    const params = {
      ...this.defaultParams,
      fields: ['id', 'translations.language', 'translations.name'],
      sort: ['id'],
    };

    return this.requestWithErrorHandling<ModuleFunction[]>('module_function', params).pipe(
      map((moduleFunctions) =>
        moduleFunctions.map((mf) => ({
          id: String(mf.id),
          translations: mf.translations ?? [],
        })),
      ),
    );
  }

  loadHostingLocations(): Observable<HostingLocation[]> {
    const params = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
    };
    return this.requestWithErrorHandling<HostingLocation[]>('hosting_location', params);
  }

  loadPaymentPlans(): Observable<PaymentPlan[]> {
    const params = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
    };
    return this.requestWithErrorHandling<PaymentPlan[]>('customer_payment_plan', params);
  }

  loadArchitecturalPrinciples(): Observable<ArchitecturalPrincipleDirectus[]> {
    const params = {
      ...this.defaultParams,
      fields: ['id', 'sort_number', 'principles', {translations: ['language', 'name']}],
      sort: ['sort_number'],
    };

    return this.requestWithErrorHandling<ArchitecturalPrincipleDirectus[]>(
      'architectural_principle',
      params,
    );
  }

  loadProviderPaymentPlans(): Observable<ProviderPaymentPlan[]> {
    const params = {
      ...this.defaultParams,
      fields: ['id', {translations: ['name', 'language']}],
      sort: ['sort_number'],
    };

    return this.requestWithErrorHandling<ProviderPaymentPlan[]>('provider_payment_plan', params);
  }

  loadTranslations(lang: string): Observable<Translation[]> {
    const filter = {language: {code: {'_eq': lang}}};
    const params = {
      ...this.defaultParams,
      filter: filter,
      limit: -1,
    };
    return this.requestWithErrorHandling<Translation[]>('translations', params);
  }

  loadHeaderMenu(): Observable<HeaderMenu[]> {
    return this.requestWithErrorHandling<HeaderMenu[]>('headerMenu', this.defaultParams);
  }

  loadFooterLinks(): Observable<FooterLink[]> {
    return this.requestWithErrorHandling<FooterLink[]>('footerLinks', this.defaultParams);
  }

  loadNonHrSystems(): Observable<NonHrSystems[]> {
    const params: DirectusRequestParams = {
      ...this.defaultParams,
      fields: ['id', {translations: ['name', 'language']}],
    };

    return this.requestWithErrorHandling<NonHrSystems[]>('non_hr_tools', params);
  }

  loadDemonstrationAgenda(): Observable<DemonstrationAgenda[]> {
    const params: DirectusRequestParams = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
      sort: ['default_sort_order'],
    };

    return this.requestWithErrorHandling<DemonstrationAgenda[]>('demonstration_agenda', params);
  }

  loadUseCasePersonas(): Observable<UseCasePersona[]> {
    const params: DirectusRequestParams = {
      ...this.defaultParams,
      fields: ['id', {translations: ['*']}],
    };
    return this.requestWithErrorHandling<UseCasePersona[]>('use_case_persona', params);
  }

  loadHrToolIds(filters?: HrToolFilters): Observable<{id: string}[]> {
    return this.hrCatalog
      .getTools(filters ? mapHrToolFilters(filters) : {})
      .pipe(map((tools) => tools.map((tool) => ({id: tool.id ?? ''}))));
  }

  loadHrToolsMini(): Observable<HrToolMini[]> {
    return this.hrCatalog.getToolsMini().pipe(map((tools) => tools.map(mapHrToolMiniResponse)));
  }

  //loadHrTools(page: number, limit: number): Observable<HrTool[]> {
  // attributes: page: page, limit: limit,
  // paginator bug in primeNG
  // retrieving data paginated would be nice, it works on the directus' side
  // however there's a bug atm on the primeNG paginator's side:
  // https://github.com/primefaces/primeng/issues/17569
  // which prevents to be able to manually set the totalRecords value for paginator, instead it
  // defaults to value.length, but if the directus pagination limit is the same as max items on one page
  // the paginator component always shows just 1 page, since it doesn't know how many total entry there are
  loadHrTools(filters?: HrToolFilters): Observable<HrTool[]> {
    return forkJoin({
      tools: this.hrCatalog.getTools(filters ? mapHrToolFilters(filters) : {}),
      references: this.hrCatalogReferenceData(),
    }).pipe(map(({tools, references}) => tools.map((tool) => mapHrToolResponse(tool, references))));
  }

  private hrCatalogReferenceData(): Observable<HrCatalogReferenceData> {
    if (!this.hrCatalogReferenceCache$) {
      this.hrCatalogReferenceCache$ = forkJoin({
        moduleFunctions: this.loadModuleFunctions(),
        countries: this.loadCountryCodes(),
        languages: this.loadLanguageCodes(),
      }).pipe(
        map(({moduleFunctions, countries, languages}) => ({
          moduleFunctionsById: new Map(
            moduleFunctions.map((moduleFunction) => [moduleFunction.id, moduleFunction]),
          ),
          countryByIso: new Map(countries.map((country) => [country.isoCode, country])),
          languageByIso: new Map(languages.map((language) => [language.isoCode, language])),
        })),
        shareReplay(1),
      );
    }
    return this.hrCatalogReferenceCache$;
  }

  loadHrToolServiceModels(): Observable<HrToolServiceModel[]> {
    return this.hrCatalog.getServiceModels().pipe(
      map((serviceModels) =>
        serviceModels.map((serviceModel) => ({
          id: serviceModel.id ?? '',
          name: serviceModel.name ?? '',
          translationKey: serviceModel.translationKey ?? null,
        })),
      ),
    );
  }

  loadModuleFunctionsGroupedByModule(): Observable<Map<number, Module>> {
    return this.loadModuleFunctions().pipe(
      map((functions: ModuleFunction[]) => {
        const moduleMap = new Map<number, Module>();
        functions.forEach((func) => {
          const moduleId = func.module_id?.id;
          if (!moduleMap.has(moduleId)) {
            moduleMap.set(moduleId, {
              id: moduleId,
              name: func.module_id?.name ?? `Module ${moduleId}`,
              translations: func.module_id?.translations,
              functions: [],
            });
          }
          moduleMap.get(moduleId)?.functions?.push(func);
        });
        return moduleMap;
      }),
    );
  }

  loadModuleFunctionOptionsGroupedByModule(): Observable<DirectusTranslatedItemGroup[]> {
    return this.loadModuleFunctionsGroupedByModule().pipe(
      map((modules) =>
        [...modules.values()].map((module) => ({
          id: String(module.id),
          translations: module.translations ?? [],
          items: module.functions.map((moduleFunction) =>
            this.toDirectusTranslatedItem(moduleFunction, (x) => String(x.id)),
          ),
        })),
      ),
    );
  }

  private moduleFunctionsCache$?: Observable<ModuleFunction[]>;

  loadModuleFunctions(): Observable<ModuleFunction[]> {
    if (!this.moduleFunctionsCache$) {
      const params = {
        ...this.defaultParams,
        fields: ['*', {translations: ['*']}, {module_id: ['*', {translations: ['*']}]}],
        sort: ['module_id.name'],
      };

      this.moduleFunctionsCache$ = this.requestWithErrorHandling<ModuleFunction[]>(
        'module_function',
        params,
      ).pipe(shareReplay(1));
    }

    return this.moduleFunctionsCache$;
  }

  loadModules(): Observable<Module[]> {
    const params = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
    };

    return this.requestWithErrorHandling<Module[]>('module', params);
  }

  loadHrToolById(
    id: string,
    options?: {requireActive?: boolean; lang?: string},
  ): Observable<HrTool | null> {
    return forkJoin({
      tool: this.hrCatalog.getTool({directusId: id}),
      references: this.hrCatalogReferenceData(),
    }).pipe(
      map(({tool, references}) => {
        if (!tool || (options?.requireActive && tool.active === false)) {
          return null;
        }
        return this.sortModuleFunctionsByLanguage(
          mapHrToolResponse(tool, references),
          options?.lang,
        );
      }),
    );
  }

  private sortModuleFunctionsByLanguage(tool: HrTool, lang?: string): HrTool {
    if (!lang) {
      return tool;
    }
    const moduleFunctions = tool.moduleFunctions.slice().sort((first, second) => {
      const firstName = first.translations?.find((t) => t.language === lang)?.name ?? first.name;
      const secondName = second.translations?.find((t) => t.language === lang)?.name ?? second.name;
      return firstName.localeCompare(secondName);
    });
    return {...tool, moduleFunctions};
  }

  loadProjectSteps(): Observable<FlattenedProjectStep[]> {
    const params: DirectusRequestParams = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
      sort: ['sort_order'],
    };
    return this.requestWithErrorHandling<ProjectStep[]>('project_steps', params).pipe(
      flattenProjectStepResponse(),
    );
  }

  loadChapters(): Observable<ProjectChapter[]> {
    return this.requestWithErrorHandling<ProjectChapter[]>('project_steps', {
      ...this.defaultParams,
      fields: ['id', 'chapterConfigId', {translations: ['name', 'language']}],
      filter: {parent: {_null: true}},
      sort: ['sort_order'],
    });
  }

  loadProjectStepDescriptions(): Observable<ProjectStepDescription[]> {
    const params: DirectusRequestParams = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
    };
    return this.requestWithErrorHandling('project_step_description', params);
  }

  loadTextTemplate(key: string): Observable<TextTemplate | null> {
    const params = {
      filter: {key: {_eq: key}},
      fields: ['*', {translations: ['*']}],
    };

    return this.requestWithErrorHandling<TextTemplate[]>('text_template', params).pipe(
      map((templates) => {
        const template = templates?.[0] ?? null;
        if (!template) {
          this.toast.showError('CMS Error', `Text template with key '${key}' not found`);
        }
        return template;
      }),
      catchError(() => of(null)),
    );
  }

  // deprecated. use #loadDirectusDropdown instead
  loadDropdownWithValues(dropdownKey: string): Observable<DirectusSelect> {
    const params = {
      ...this.defaultParams,
      filter: {key: {_eq: dropdownKey}},
      fields: ['*', {translations: ['*']}, {values: ['*', {translations: ['*']}]}],
    };

    return this.requestWithErrorHandling<DirectusSelect[]>('dropdown', params).pipe(
      map((array) => {
        const item = array[0]!;
        return {
          id: item.id,
          key: item.key,
          translations: item.translations,
          values: item.values.map((v: DirectusSelectValue) => ({
            id: v.id,
            parentValue: v.parentValue,
            translations: v.translations,
          })),
        };
      }),
    );
  }

  loadDirectusDropdown(dropdownKey: string): Observable<DirectusTranslatedItem[]> {
    const params = {
      ...this.defaultParams,
      filter: {key: {_eq: dropdownKey}},
      fields: [
        'values.id',
        'values.sort_number',
        'values.translations.language',
        'values.translations.name',
        'values.tooltip.key',
      ],
      deep: {values: {_sort: ['sort_number']}},
    };

    return this.requestWithErrorHandling<DirectusDropdown[]>('dropdown', params).pipe(
      map((result) => result?.[0]?.values ?? []),
      map((values) =>
        values.map((v) => ({
          ...v,
          filterText: v.translations.map((x) => x.name).join(' -'),
        })),
      ),
    );
  }

  loadDesignCriteria(): Observable<DesignCriteria[]> {
    const params = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
    };

    return this.requestWithErrorHandling<DesignCriteria[]>('design_criteria', params);
  }

  loadEvaluationCriteria(): Observable<EvaluationCriteria[]> {
    if (!this.evaluationCriteriaCache$) {
      const params = {
        ...this.defaultParams,
        fields: ['*', {translations: ['*']}],
      };

      this.evaluationCriteriaCache$ = this.requestWithErrorHandling<EvaluationCriteria[]>(
        'criteria',
        params,
      ).pipe(shareReplay(1));
    }

    return this.evaluationCriteriaCache$;
  }

  loadSubCriteria(): Observable<EvaluationSubCriteriaDirectus[]> {
    if (!this.subCriteriaCache$) {
      const params = {
        ...this.defaultParams,
        fields: ['*', {translations: ['*']}],
      };

      this.subCriteriaCache$ = this.requestWithErrorHandling<EvaluationSubCriteriaDirectus[]>(
        'subcriteria',
        params,
      ).pipe(shareReplay(1));
    }

    return this.subCriteriaCache$;
  }

  loadSubCriteriaAttributes(): Promise<SubcriteriaAttributeDirectus[]> {
    if (!this.subCriteriaAttributesCache$) {
      const params = {
        ...this.defaultParams,
        fields: ['*', {translations: ['*']}],
      };

      this.subCriteriaAttributesCache$ = this.requestWithErrorHandling<
        SubcriteriaAttributeDirectus[]
      >('subcriteria_attribute', params).pipe(shareReplay(1));
    }

    return firstValueFrom(this.subCriteriaAttributesCache$);
  }

  async loadSubCriteriaAttributesBySubCriteria(
    criteriaId?: string,
  ): Promise<SubcriteriaAttributeDirectus[]> {
    const allAttributes = await this.loadSubCriteriaAttributes();

    if (!criteriaId) {
      return allAttributes;
    }

    return allAttributes.filter((attr) => attr.subcriteria === criteriaId);
  }

  loadTooltipByKey(tooltipKey: string): Observable<Tooltip | null> {
    const params = {
      ...this.defaultParams,
      filter: {key: {_eq: tooltipKey}},
      fields: ['*', {translations: ['*']}],
    };

    return this.requestWithErrorHandling<Tooltip[]>('tooltip', params).pipe(
      map((array) => (array.length > 0 ? array[0] : null)),
    );
  }

  loadTooltipsByKeyPrefix(tooltipKeyPrefix: string): Observable<Tooltip[]> {
    const params = {
      ...this.defaultParams,
      filter: {key: {_starts_with: tooltipKeyPrefix}},
      fields: ['*', {translations: ['*']}],
    };

    return this.requestWithErrorHandling<Tooltip[]>('tooltip', params);
  }

  loadProjectTimeFrames(): Observable<ProjectTimeFrameDirectus[]> {
    const params = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
    };

    return this.requestWithErrorHandling<ProjectTimeFrameDirectus[]>('project_time_frame', params);
  }

  loadAttributeFulfillmentTypes(): Promise<AttributeFulfillmentTypeDirectus[]> {
    if (!this.attributeFulfillmentTypesCache$) {
      const params = {
        ...this.defaultParams,
        fields: ['*', {translations: ['*']}],
      };

      this.attributeFulfillmentTypesCache$ = this.requestWithErrorHandling<
        AttributeFulfillmentTypeDirectus[]
      >('attribute_fulfillment_type', params).pipe(
        map((response) => {
          return response.map((type) => ({
            ...type,
            translations: type.translations || [],
          }));
        }),
        shareReplay(1),
      );
    }

    return firstValueFrom(this.attributeFulfillmentTypesCache$);
  }

  loadHrToolsByIds(hrToolIds: string[]): Observable<HrTool[]> {
    if (!hrToolIds || hrToolIds.length === 0) {
      return of([]);
    }

    const requestedIds = new Set(hrToolIds);
    return this.loadHrTools().pipe(
      map((tools) => tools.filter((tool) => requestedIds.has(tool.id))),
    );
  }

  loadInvitationUploadDocuments(): Observable<InvitationUploadDocument[]> {
    const params = {
      ...this.defaultParams,
      fields: ['*', {translations: ['*']}],
      sort: ['sort'],
    };

    return this.requestWithErrorHandling<any[]>('invitation_upload_documents', params).pipe(
      map((documents) =>
        documents.map((doc) => ({
          ...doc,
          translations: doc.translations.map((t: any) => ({
            ...t,
            fileType: t.file_type, // now TS allows this
          })),
        })),
      ),
    );
  }

  loadRfxMainReasonsByDecisionType(
    decisionType: 'participant' | 'rejected',
    rfxStage: RfxStage,
  ): Observable<RfxMainReason[]> {
    const params: DirectusRequestParams = {
      ...this.defaultParams,
      filter: {
        decision_type: {_eq: decisionType},
        rfx_stage: {_eq: rfxStage},
        status: {_eq: 'published'},
      },
      fields: ['*', {text_intro: ['*']}, {detail_reasons: ['*', {text_fragment: ['*']}]}],
      sort: ['sort'],
    };

    return this.requestWithErrorHandling<RfxMainReason[]>('rfx_main_reasons', params);
  }

  loadRfxDetailReasonsByMainReason(mainReasonId: string): Observable<RfxDetailReason[]> {
    const params: DirectusRequestParams = {
      ...this.defaultParams,
      filter: {main_reason: {_eq: mainReasonId}, status: {_eq: 'published'}},
      fields: ['*', {text_fragment: ['*']}, {main_reason: ['*', {text_intro: ['*']}]}],
      sort: ['sort'],
    };

    return this.requestWithErrorHandling<RfxDetailReason[]>('rfx_detail_reasons', params).pipe(
      map((items) =>
        (items ?? []).map((item) => ({
          ...item,
          text_fragment: item.text_fragment ?? [],
          mainReason:
            (item as RfxDetailReason & {main_reason?: RfxMainReason | null}).main_reason ?? null,
        })),
      ),
    );
  }

  loadRfxTextConnectorsByDecisionType(
    decisionType: 'participant' | 'rejected',
  ): Observable<RfxTextConnector[]> {
    const params: DirectusRequestParams = {
      ...this.defaultParams,
      filter: {decision_type: {_eq: decisionType}, status: {_eq: 'published'}},
      fields: ['*', {translations: ['*']}],
      sort: ['sort'],
    };

    return this.requestWithErrorHandling<RfxTextConnector[]>('rfx_text_connectors', params).pipe(
      map((items) =>
        (items ?? []).map((item) => ({
          ...item,
          translations: item.translations ?? [],
        })),
      ),
    );
  }

  loadComposableModuleFunctionIdsByHrToolIds(
    hrToolIds: string[],
  ): Observable<Map<string, number[]>> {
    if (!hrToolIds || hrToolIds.length === 0) {
      return of(new Map<string, number[]>());
    }

    return this.hrCatalog
      .getComposableModuleFunctions({hrToolIds})
      .pipe(map((byToolId) => new Map(Object.entries(byToolId ?? {}))));
  }

  private requestWithErrorHandling<T>(
    collection: DirectusCollection,
    params: DirectusRequestParams,
  ): Observable<T> {
    return from(this.directus.request<T>(readItems(collection, params))).pipe(
      catchError((err) => this.handleError(collection, err)),
    );
  }

  private handleError(domain: string, err: Error) {
    console.error(`Error loading ${domain} from Directus`, err);
    this.toast.showError('CMS Error', `Could not load ${domain}`);
    return throwError(() => err);
  }
}
